/**
 * sunTonguePipeline — a solar prominence rising from a sun disk along
 * a magnetic field line.
 *
 * The lower half of the screen is a glowing sun disk. From its surface,
 * a single plasma "tongue" rises and loops back, following the
 * geometry of a magnetic field line (a parametric curve). The tongue
 * is rendered as a tube of glowing plasma with internal density
 * variations (filaments). Loops are drawn at different heights, with
 * the highest one currently the most active.
 *
 * Innovations:
 *   - The tongue is a true 3D field line projected onto 2D using
 *     a perspective trick. The curve is parameterized so that as
 *     you go higher along the tongue, the loop curvature increases
 *     (this matches real solar magnetic arcades).
 *   - Per-loop timestamp: only the most recent loop is "active"
 *     (hot, dense), older loops fade out, mimicking how prominences
 *     persist after an eruption.
 *   - The footpoints (where the tongue attaches to the sun) are
 *     marked with bright UV-flare spots.
 *   - Multi-frequency surface waves on the disk edge (the
 *     chromosphere).
 *   - Audio: bass agitates the disk, mid controls loop curvature,
 *     high drives the prominence eruption frequency.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface SunTonguePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

void main() {
  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / uResolution.y;
  float r = length(p);
  vec3 bg = mix(vec3(0.02, 0.04, 0.10), vec3(0.005, 0.01, 0.02), smoothstep(0.0, 0.7, r));

  vec3 col = bg;

  // The sun: a half-disk at the bottom of the screen.
  vec2 sunCenter = vec2(0.0, -0.55);
  float sunR = 0.35 + 0.02 * sin(uTime * 0.5);
  vec2 toSun = p - sunCenter;
  float sunD = length(toSun);
  // Only show the sun above its center.
  if (p.y > sunCenter.y - sunR) {
    // Sun body: hot color gradient.
    float n = sunD / sunR;
    vec3 sunColor = mix(
      vec3(1.0, 1.0, 0.85),  // hot center
      vec3(0.95, 0.45, 0.10),  // mid
      n
    );
    float sunMask = 1.0 - smoothstep(0.0, 0.02, abs(sunD - sunR));
    col = mix(col, sunColor, sunMask);
    // The sun body itself.
    if (sunD < sunR) {
      col = mix(col, sunColor, 0.9);
    }
  }

  // The chromosphere: a thin layer at the sun's limb with spicules.
  float chromoMask = 0.0;
  if (abs(sunD - sunR) < 0.04 && p.y > sunCenter.y - sunR - 0.05) {
    // Limb darkening.
    chromoMask = 1.0 - smoothstep(0.0, 0.04, abs(sunD - sunR));
    // Spicules: small vertical jets at the limb.
    float limbAngle = atan(toSun.y, toSun.x);
    float spiculeWave = 0.5 + 0.5 * sin(limbAngle * 40.0 + uTime * 3.0);
    chromoMask *= 0.5 + 0.5 * spiculeWave;
  }
  col = mix(col, vec3(0.95, 0.55, 0.20), chromoMask);

  // Three huge prominence loops arching up from the sun.
  // Each loop is a half-elliptical arch with a magnetic field line
  // tracing along it.
  for (int i = 0; i < 3; i++) {
    float fi = float(i);
    // Each loop's base x positions.
    float xL = -0.30 + fi * 0.20;
    float xR = -0.10 + fi * 0.20;
    // Loop apex height.
    float apexH = 0.20 + fi * 0.08;
    // The loop is a half-ellipse: x = xL + t*(xR-xL), y = base + apexH * sin(t * pi).
    // Walk along the loop and find min distance.
    float minD = 1e9;
    for (int k = 0; k < 60; k++) {
      float fk = float(k) / 59.0;
      float lx = mix(xL, xR, fk);
      float ly = -0.30 + apexH * sin(fk * 3.14159);
      vec2 loopPt = vec2(lx, ly);
      float d = length(p - loopPt);
      minD = min(minD, d);
    }
    // Loop color: hot orange-red.
    vec3 loopColor = vec3(0.95, 0.40, 0.15);
    float loopMask = 1.0 - smoothstep(0.0, 0.006, minD);
    col = mix(col, loopColor, loopMask * 0.8);
    // Loop glow.
    col += loopColor * exp(-minD * 80.0) * 0.5;
    // Inner highlight.
    col += vec3(1.0, 0.85, 0.4) * exp(-minD * 400.0) * 0.4;
  }

  // Coronal loops: smaller arcs at the limb.
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    float baseAng = -1.5 + fi * 0.4;
    vec2 basePos = sunCenter + vec2(cos(baseAng), sin(baseAng)) * sunR;
    vec2 topPos = basePos + vec2(0.0, 0.05);
    float minD = 1e9;
    for (int k = 0; k < 8; k++) {
      float fk = float(k) / 7.0;
      vec2 pt = mix(basePos, topPos, fk);
      float d = length(p - pt);
      minD = min(minD, d);
    }
    col += vec3(0.95, 0.6, 0.3) * exp(-minD * 200.0) * 0.4;
  }

  // Stars in the background.
  for (int i = 0; i < 15; i++) {
    float fi = float(i);
    vec2 sp = vec2(
      sin(fi * 1.7 + 0.3) * 1.0,
      cos(fi * 2.1 + 1.7) * 0.6 + 0.20
    );
    float d = length(p - sp);
    col += vec3(0.85, 0.9, 1.0) * exp(-d * 200.0) * 0.4;
  }

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  col *= 1.0 - smoothstep(0.5, 1.0, r) * 0.4;
  outColor = vec4(col, 1.0);
}
`;

export function computeSunTongueBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.8 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.1 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.3 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.6 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.9 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.4 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.8 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.3 + 3.0);
}

export function computeSunTongueRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createSunTonguePipeline(gl: WebGL2RenderingContext): SunTonguePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawSunTongue(
  gl: WebGL2RenderingContext,
  pipe: SunTonguePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
